import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Pages } from '../Pages'
import { MenuItems } from '../enums/MenuItems'
import tenantApi from '../api/tenant/TenantApi'
import { AdminTenant } from '../api/tenant/models/adminGet/AdminGetRes'
import UserRolePill from '../components/UserRolePill'
import { UserRoles } from '../api/tenant/models/UserDto'
import SearchPeopleModal from '../components/SearchPeopleModal'
import { PersonDto } from '../api/person/models/PersonDto'
import PersonUi from '../components/PersonUi'
import LayoutSingleView from '../components/layout/LayoutSingleView'

function CreateUser(): React.JSX.Element {
    const [email, setEmail] = useState<string>('')
    const [isSearchPersonModalOpen, setIsSearchPersonModalOpen] =
        useState(false)
    const [linkedPerson, setLinkedPerson] = useState<PersonDto>()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const tenantId = searchParams.get('tenantId')
    const [tenant, setTenant] = useState<AdminTenant>()
    const [selectedRoles, setSelectedRoles] = useState<UserRoles[]>([])

    const refreshTenant = useCallback(async () => {
        if (!tenantId) {
            return
        }

        const tenant = (await tenantApi.adminGet(tenantId)).tenant
        setTenant(tenant)
    }, [tenantId])

    useEffect(() => {
        refreshTenant()
    }, [tenantId, refreshTenant])

    function handleRoleChange(
        event: React.ChangeEvent<HTMLInputElement>,
    ): void {
        const role = event.target.value as UserRoles
        if (event.target.checked) {
            // Add role to the selected roles
            setSelectedRoles((prev) => [...prev, role])
        } else {
            // Remove role from the selected roles
            setSelectedRoles((prev) => prev.filter((r) => r !== role))
        }
    }

    async function saveUser() {
        if (!tenant) {
            throw new Error(
                `Unable to save user ${email} for tenant ${tenantId}. Tenant not found.`,
            )
        }

        await tenantApi.createUser({
            email: email,
            tenantId: tenant.id,
            userRoles: selectedRoles,
            personId: linkedPerson?.id,
        })
        navigate(`${Pages.Tenant}?id=${tenant?.id}`)
    }

    function emailChange(event: React.ChangeEvent<HTMLInputElement>): void {
        setEmail(event.target.value)
    }

    async function handleSelectPerson(person: PersonDto) {
        setLinkedPerson(person)
        setIsSearchPersonModalOpen(false)
    }

    async function handleRemovePerson() {
        setLinkedPerson(undefined)
        setIsSearchPersonModalOpen(false)
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Tenants}>
            <>
                {!tenant && <div>Loading...</div>}
                {tenant && (
                    <div>
                        <h1>Create New User for {tenant?.name}</h1>
                        <div>
                            <div>
                                <label htmlFor="email">Email*</label>
                                <input
                                    id="email"
                                    type="text"
                                    placeholder="Email..."
                                    value={email}
                                    onChange={emailChange}
                                    className="form-input"
                                ></input>
                            </div>
                            <div className="space-y-2 mt-4">
                                <div>
                                    <label>
                                        <input
                                            type="checkbox"
                                            id="admin"
                                            name="roles"
                                            value={UserRoles.ADMIN}
                                            onChange={handleRoleChange}
                                            className="mr-2"
                                        />
                                        <UserRolePill value={UserRoles.ADMIN} />
                                    </label>
                                </div>
                                <div>
                                    <label>
                                        <input
                                            type="checkbox"
                                            id="hiring-manager"
                                            name="roles"
                                            value={UserRoles.HM}
                                            onChange={handleRoleChange}
                                            className="mr-2"
                                        />
                                        <UserRolePill value={UserRoles.HM} />
                                    </label>
                                </div>
                                <div>
                                    <label>
                                        <input
                                            type="checkbox"
                                            id="talent-acquisition"
                                            name="roles"
                                            value={UserRoles.TA}
                                            onChange={handleRoleChange}
                                            className="mr-2"
                                        />
                                        <UserRolePill value={UserRoles.TA} />
                                    </label>
                                </div>
                            </div>
                            <div className="mt-4">
                                <label htmlFor="personId">
                                    Link to Person (imported from Linked In)
                                </label>
                                {linkedPerson && (
                                    <div>
                                        <div>
                                            Selected person:{' '}
                                            <PersonUi person={linkedPerson} />
                                        </div>
                                        <button
                                            className="remove-button"
                                            onClick={handleRemovePerson}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                                <div>
                                    <button
                                        className="secondary-button"
                                        onClick={() =>
                                            setIsSearchPersonModalOpen(true)
                                        }
                                    >
                                        Select Person
                                    </button>
                                </div>
                            </div>
                            <div className="form-buttons">
                                <button
                                    className="primary-button mt-4"
                                    onClick={saveUser}
                                >
                                    Save
                                </button>
                                <button
                                    className="danger-button"
                                    onClick={() =>
                                        navigate(
                                            `${Pages.Tenant}?id=${tenant?.id}`,
                                        )
                                    }
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                        {isSearchPersonModalOpen && (
                            <SearchPeopleModal
                                onClose={() =>
                                    setIsSearchPersonModalOpen(false)
                                }
                                onSelect={handleSelectPerson}
                            />
                        )}
                    </div>
                )}
            </>
        </LayoutSingleView>
    )
}

export default CreateUser
